import React from 'react';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  TextField,
  Stack,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cancel, Download, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { format } from 'date-fns';
import { DownloadJob, JobStatus, PAYER_TYPE_LABELS, DOC_TYPE_OPTIONS } from './mock-data';
import documentBundleService from 'src/services/apiServices/document-bundle/document-bundle-service';

const DOC_TYPE_LABEL_MAP = Object.fromEntries(DOC_TYPE_OPTIONS.map((o) => [o.value, o.label]));

/**
 * SQ-174: a readable status chip.
 *
 * MUI's filled success chip is white on #10B981 — about 2.8:1, where a 12px label needs
 * 4.5:1. The ticket asks for "the soft variant", which this MUI does not have: 5.13.7 offers
 * `filled` and `outlined` only, and no theme override adds a third. `outlined` does not solve
 * it either, since it puts the same mid-tone hue on white as text.
 *
 * So the soft treatment is built here rather than named: the hue at low alpha behind it, and
 * the hue's dark shade as the label. Both come from whichever palette colour the chip was
 * already given, so the mapping from status to colour is untouched and a new status needs no
 * work here.
 */
type ChipColor = 'default' | 'info' | 'success' | 'error' | 'warning';

const softChipSx = (color: ChipColor) => ({
  //  `default` has no main/dark pair on the palette, so it takes the grey ramp rather than
  //  being special-cased at the call site.
  bgcolor: (theme: Theme) =>
    alpha(color === 'default' ? theme.palette.grey[600] : theme.palette[color].main, 0.16),
  //  `darkest`, not `dark`. Measured against the rendered chip over a white paper: `dark`
  //  reaches 4.80 on info and 5.33 on error but only 4.21 on success and 3.93 on default --
  //  and success is the very chip the complaint was about, so stopping there would have
  //  closed the ticket without fixing the thing it was raised for. `darkest` clears every
  //  colour comfortably (7.99-9.84). It is optional in the palette augmentation, hence the
  //  fall-back.
  color: (theme: Theme) =>
    color === 'default'
      ? theme.palette.neutral?.[700] ?? theme.palette.text.primary
      : theme.palette[color].darkest ?? theme.palette[color].dark,
  fontWeight: 600,
});
const STATUS_CHIP: Record<
  JobStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }
> = {
  QUEUED: { label: 'Queued', color: 'default' },
  PROCESSING: { label: 'Processing', color: 'info' },
  IN_PROGRESS: { label: 'Processing', color: 'info' },
  COMPLETE: { label: 'Ready for Download', color: 'success' },
  COMPLETED: { label: 'Ready for Download', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'warning' },
};

const STATUS_SORT_ORDER: Record<JobStatus, number> = {
  PROCESSING: 0,
  IN_PROGRESS: 0,
  QUEUED: 1,
  COMPLETE: 2,
  COMPLETED: 2,
  FAILED: 3,
  CANCELLED: 4,
};

type SortField = 'created_at' | 'status';
type SortDir = 'asc' | 'desc';

const formatBytes = (bytes?: number) => {
  if (!bytes) return '—';
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  return `${(bytes / 1e6).toFixed(1)} MB`;
};

const DATE_SCOPE_LABELS: Record<string, string> = {
  range: 'Date range',
  episode: 'Full episode',
  payer: 'Payer period',
};

const DOC_HANDLING_LABELS: Record<string, string> = {
  subfolder: 'Place in subfolder',
  merge: 'Single merged PDF',
};

const ORDER_BY_LABELS: Record<string, string> = {
  date_discipline: 'Date → Discipline',
  discipline_date: 'Discipline → Date',
};

/**
 * SQ-184: history reports what was asked for, so it has to name values the dialog no
 * longer offers — every job requested before Physician was dropped still carries
 * `group_by: "physician"`. Unknown values are capitalised rather than hidden, and a
 * missing one renders an em dash: `capitalize(undefined)` threw, which took the whole
 * table down over one absent field.
 */
const GROUP_BY_LABELS: Record<string, string> = {
  patient: 'Resident',
  therapist: 'Therapist',
  physician: 'Physician',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const groupByLabel = (value?: string) =>
  value ? GROUP_BY_LABELS[value] ?? capitalize(value) : '—';

interface Props {
  jobs: DownloadJob[];
  loading: boolean;
  onCancel: (jobId: string) => void;
}

export const JobHistoryTable: React.FC<Props> = ({ jobs, loading, onCancel }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sortField, setSortField] = React.useState<SortField>('created_at');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [expandedRow, setExpandedRow] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');

  //  SQ-174: "if you pulled a bundle for a specific ADR and came back a week later, how
  //  would you find it again?" — the answer is to type the reference. The file name matches
  //  too, because the reference is optional and a job without one still has to be findable.
  //
  //  Not the resident, though the ticket asks for it: nothing on a job row carries a name or
  //  an MRN. `patient_count` is a number and `patient_ids` are uuids, so resident search
  //  needs provider-service to return names on the job list and is not in this change.
  //
  //  Filtering before sorting and paging, so the result is "every match", not "the matches
  //  that happen to be on this page".
  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return jobs;
    return jobs.filter((job) => {
      const reference = job.request_details?.adr_reference ?? '';
      return (
        job.job_name.toLowerCase().includes(needle) || reference.toLowerCase().includes(needle)
      );
    });
  }, [jobs, query]);

  // No usePresignedUrl hook — download is handled directly via service call

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'created_at') {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else {
      cmp = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDownload = async (job: DownloadJob) => {
    setDownloadingId(job.job_id);
    try {
      const response = await documentBundleService.getPresignedUrl(job.job_id);
      const url =
        response?.data?.data?.url ??
        response?.data?.data?.presigned_url ??
        response?.data?.url ??
        response?.data?.presigned_url;
      if (url) {
        // Trigger download via hidden anchor — no page navigation
        const a = document.createElement('a');
        a.href = url;
        a.download = (job.job_name || 'download').replace(/[^a-zA-Z0-9_-]/g, '_') + '.zip';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      // Best-effort — don't crash the page
      console.error('Download failed:', e);
    }
    setDownloadingId(null);
  };

  const toggleExpand = (jobId: string) => {
    setExpandedRow((prev) => (prev === jobId ? null : jobId));
  };

  return (
    <Paper
      sx={{ overflow: 'hidden' }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 1, pb: 1 }}
      >
        <TextField
          hiddenLabel
          size="small"
          value={query}
          onChange={(e) => {
            //  Page reset here rather than in an effect on `query`: an effect runs a render
            //  later, so there is one frame where the list has narrowed but the page index
            //  has not, and `paginated` slices past the end — a flash of the empty state.
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Search by reference or file name"
          inputProps={{ 'aria-label': 'Search document requests' }}
          sx={{ maxWidth: 320, width: '100%' }}
        />
        {/*  SQ-157 scopes the history to the facility in the top nav, and saying so matters:
            a list that hides another building's jobs is otherwise indistinguishable from one
            that is simply empty. The scoping happens at the parent, which decides what
            `jobs` holds -- this line reports it rather than enforces it, so if that ever
            stops being true the claim goes stale silently. A test at the parent is the
            thing that would catch it. */}
        <Typography
          variant="caption"
          color="text.disabled"
        >
          Showing requests for the facility selected above
          {query.trim()
            ? ` · ${filtered.length} of ${jobs.length} ${
                filtered.length === 1 ? 'matches' : 'match'
              }`
            : ''}
        </Typography>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ width: 36, p: 0.5 }} />
              <TableCell sx={{ fontWeight: 600 }}>Job Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDir : 'desc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 180 }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Documents</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 180 }}>
                <TableSortLabel
                  active={sortField === 'created_at'}
                  direction={sortField === 'created_at' ? sortDir : 'desc'}
                  onClick={() => handleSort('created_at')}
                >
                  Requested Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, width: 80 }}
                align="right"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontStyle="italic"
                  >
                    {query.trim()
                      ? `No document requests match "${query.trim()}".`
                      : 'No document requests yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((job) => {
                const { label, color } = STATUS_CHIP[job.status];
                const isActive =
                  job.status === 'QUEUED' ||
                  job.status === 'PROCESSING' ||
                  job.status === 'IN_PROGRESS';
                const isDownloading = downloadingId === job.job_id;
                const isExpanded = expandedRow === job.job_id;
                const hasDetails = !!job.request_details;

                return (
                  <React.Fragment key={job.job_id}>
                    <TableRow hover>
                      <TableCell sx={{ p: 0.5 }}>
                        {hasDetails && (
                          <IconButton
                            size="small"
                            aria-label={
                              isExpanded ? 'Hide request details' : 'Show request details'
                            }
                            onClick={() => toggleExpand(job.job_id)}
                          >
                            {isExpanded ? (
                              <KeyboardArrowDown fontSize="small" />
                            ) : (
                              <KeyboardArrowRight fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                        >
                          {job.job_name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={label}
                          color={color}
                          size="small"
                          sx={softChipSx(color)}
                        />
                      </TableCell>

                      <TableCell>
                        {isActive ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={job.progress_pct}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 30 }}
                            >
                              {job.progress_pct}%
                            </Typography>
                          </Box>
                        ) : job.status === 'COMPLETE' || job.status === 'COMPLETED' ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            100%
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {job.document_count > 0
                            ? `${job.document_count} docs · ${
                                job.patient_count
                              } patients · ${formatBytes(job.size_bytes)}`
                            : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {format(new Date(job.created_at), 'MMM d, yyyy h:mm a')}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {/*  The parentheses are load-bearing: && binds tighter than ||, so
                              without them this reads `a || (b && X)` and a COMPLETE job
                              evaluates to `true`, which React renders as nothing. COMPLETE
                              rows had no download button at all. */}
                          {(job.status === 'COMPLETE' || job.status === 'COMPLETED') && (
                            <Tooltip title="Download (link valid 10 min)">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(job)}
                                  disabled={isDownloading}
                                  color="primary"
                                >
                                  {isDownloading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Download fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {isActive && (
                            <Tooltip title="Cancel job">
                              <IconButton
                                size="small"
                                onClick={() => onCancel(job.job_id)}
                                color="error"
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expandable details row */}
                    {hasDetails && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}
                        >
                          <Collapse
                            in={isExpanded}
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                px: 3,
                                py: 2,
                                backgroundColor: 'grey.50',
                                display: 'flex',
                                gap: 6,
                              }}
                            >
                              {/* Filters Applied */}
                              <Box>
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="text.secondary"
                                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                >
                                  Filters Applied
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.75,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.4,
                                  }}
                                >
                                  <DetailRow
                                    label="Date range"
                                    value={`${job.request_details!.filters_applied.date_from} – ${
                                      job.request_details!.filters_applied.date_to
                                    }`}
                                  />
                                  {job.request_details!.filters_applied.payer_type && (
                                    <DetailRow
                                      label="Payer type"
                                      value={
                                        PAYER_TYPE_LABELS[
                                          job.request_details!.filters_applied.payer_type
                                        ] ?? job.request_details!.filters_applied.payer_type
                                      }
                                    />
                                  )}
                                  {job.request_details!.filters_applied.payer_name && (
                                    <DetailRow
                                      label="Payer name"
                                      value={job.request_details!.filters_applied.payer_name}
                                    />
                                  )}
                                  {job.request_details!.filters_applied.discipline?.length ? (
                                    <DetailRow
                                      label="Discipline"
                                      value={
                                        job.request_details!.filters_applied.discipline.length >= 3
                                          ? 'ALL'
                                          : job.request_details!.filters_applied.discipline.join(
                                              ', '
                                            )
                                      }
                                    />
                                  ) : null}
                                  {job.request_details!.filters_applied.doc_type?.length ? (
                                    <DetailRow
                                      label="Doc types"
                                      value={
                                        job.request_details!.filters_applied.doc_type.length >=
                                        DOC_TYPE_OPTIONS.length
                                          ? 'ALL'
                                          : job
                                              .request_details!.filters_applied.doc_type.map(
                                                (v) => DOC_TYPE_LABEL_MAP[v] ?? v
                                              )
                                              .join(', ')
                                      }
                                    />
                                  ) : null}
                                  {job.request_details!.filters_applied.patient_ids?.length && (
                                    <DetailRow
                                      label="Patients"
                                      value={`${
                                        job.request_details!.filters_applied.patient_ids.length
                                      } selected`}
                                    />
                                  )}
                                </Box>
                              </Box>

                              <Divider
                                orientation="vertical"
                                flexItem
                              />

                              {/* File Organization */}
                              <Box>
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="text.secondary"
                                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                >
                                  File Organization
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.75,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.4,
                                  }}
                                >
                                  <DetailRow
                                    label="Date scope"
                                    value={
                                      DATE_SCOPE_LABELS[
                                        job.request_details!.file_organization.date_scope
                                      ] ?? job.request_details!.file_organization.date_scope
                                    }
                                  />
                                  <DetailRow
                                    label="Group by"
                                    value={groupByLabel(
                                      job.request_details!.file_organization.group_by
                                    )}
                                  />
                                  <DetailRow
                                    label="Documents"
                                    value={
                                      DOC_HANDLING_LABELS[
                                        job.request_details!.file_organization.document_handling
                                      ] ?? job.request_details!.file_organization.document_handling
                                    }
                                  />
                                  <DetailRow
                                    label="Order by"
                                    value={
                                      ORDER_BY_LABELS[
                                        job.request_details!.file_organization.order_by
                                      ] ?? job.request_details!.file_organization.order_by
                                    }
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/*  Keyed off the filtered list, like its own count. Keying visibility off `jobs` and
          count off `sorted` put a pager reading "0-0 of 0" under the empty state. */}
      {!loading && sorted.length > 0 && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      )}
    </Paper>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ minWidth: 90 }}
    >
      {label}:
    </Typography>
    <Typography variant="caption">{value}</Typography>
  </Box>
);
